<template>
  <div
    class="floating-dim"
    :class="isFloating ? 'on' : ''"
    @click="outSideClick"
    :style="isFloating ? 'z-index:999' : 'z-index:-1'"
  >
    <!-- 260612 리뉴얼: 가로 3원형(Figma main_typeC) — 시안 제공 아이콘 에셋 그대로 사용 -->
    <ul class="share-menu">
      <li class="share-menu__item share-menu__item--intro" @click="fnSend('catalog')">
        <img class="share-menu__ico" src="@/assets/static/images/renewal/share-intro.png" alt="" />
        <span class="share-menu__label">기업소개</span>
      </li>
      <li class="share-menu__item share-menu__item--address" @click="fnSend('address')">
        <img class="share-menu__ico" src="@/assets/static/images/renewal/share-address.png" alt="" />
        <span class="share-menu__label">기업주소</span>
      </li>
      <li class="share-menu__item share-menu__item--card" @click="fnSend('card')">
        <img class="share-menu__ico" src="@/assets/static/images/renewal/share-card.png" alt="" />
        <span class="share-menu__label">명함</span>
      </li>
    </ul>

    <!-- 비즈니스 선택 모달 -->
    <div class="business-select-modal" v-if="showSelectModal" @click.stop>
      <div class="modal-content">
        <h3 class="modal-title">비즈니스 선택</h3>
        <ul class="business-select-list">
          <li
            v-for="business in myBusinessList"
            :key="business.businessNo"
            @click="onSelectBusiness(business)"
            class="business-select-item"
          >
            <p class="business-name">{{ business.name }}</p>
            <p class="business-address">{{ business.roadAddress || business.address }}</p>
          </li>
        </ul>
        <button class="btn border-gray medium mt16" @click="showSelectModal = false">취소</button>
      </div>
    </div>

    <!-- 명함 선택 모달 -->
    <div class="business-select-modal" v-if="showCardSelectModal" @click.stop>
      <div class="modal-content">
        <h3 class="modal-title">명함 선택</h3>
        <ul class="business-select-list">
          <li
            v-for="card in cardList"
            :key="card.cardNo"
            @click="onSelectCard(card)"
            class="business-select-item"
          >
            <p class="business-name">{{ card.name }}</p>
            <p class="business-address">{{ card.company ? card.company : '' }}{{ card.position ? ' | ' + card.position : '' }}</p>
          </li>
        </ul>
        <button class="btn border-gray medium mt16" @click="showCardSelectModal = false">취소</button>
      </div>
    </div>

    <!-- 공유 방식 선택 모달 (카카오톡 / 문자) -->
    <div class="business-select-modal" v-if="showShareMethodModal" @click.stop="showShareMethodModal = false">
      <div class="modal-content" @click.stop>
        <h3 class="modal-title">공유 방식 선택</h3>
        <ul class="business-select-list">
          <li class="business-select-item share-method-item" @click="executeShareMethod('kakao')">
            <p class="business-name">카카오톡으로 공유</p>
          </li>
          <li class="business-select-item share-method-item" @click="executeShareMethod('sms')">
            <p class="business-name">문자(SMS)로 공유</p>
          </li>
        </ul>
        <button class="btn border-gray medium mt16" @click="showShareMethodModal = false">취소</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import store from '@/store';
import { useRouter } from 'vue-router';
import { showTextModal } from '@/common/utils/ModalState';
import { isEmpty, isApp, isIPad, isIPhone, getImagePath } from '@/common/utils/Util';
import { CODE_ENV_LIST } from '@/common/constants/ConstCode';
import { nativeShareText } from '@/common/utils/module/NativeRequest';
import router from '@/router';

const { push } = useRouter();

const isFloating = computed(() => store.getters['layout/getIsFloating']);
const pageInfo = computed(() => store.getters['business/getPageInfo']);
const myBusinessList = computed(() => store.getters['business/getBusinessList']);

const showSelectModal = ref(false);
const showCardSelectModal = ref(false);
const showShareMethodModal = ref(false);
const cardList = ref([]);
const pendingSendType = ref(null);
const pendingShareData = ref(null); // { title, description, imageUrl, shareUrl, buttonText }

const outSideClick = event => {
  const menuEl = event.currentTarget.querySelector('.share-menu');
  if (menuEl && !menuEl.contains(event.target)) {
    store.commit('layout/setIsFloating');
    showSelectModal.value = false;
    showCardSelectModal.value = false;
    showShareMethodModal.value = false;
  }
};

// 1. 로그인 체크 + 비즈니스 등록 체크
const fnSend = async (type) => {
  const user = store.getters['user/getUserData'];

  // 로그인 체크
  if (!user.userNo) {
    store.commit('layout/setIsFloating', false);
    showTextModal('로그인 후 이용가능합니다.', '확인', () => {
      router.push('/signin');
    });
    return;
  }

  // 명함 보내기는 비즈니스 불필요 — 별도 처리
  if (type === 'card') {
    await store.dispatch('card/getMyCards');
    const myCards = store.getters['card/getMyCards'];

    if (!myCards || myCards.length === 0) {
      store.commit('layout/setIsFloating', false);
      showTextModal('명함 등록 후 사용 가능합니다.', '확인', () => {
        router.push('/card/register');
      });
      return;
    }

    if (myCards.length === 1) {
      sendCard(myCards[0]);
    } else {
      // 명함이 여러 개면 선택 모달 — 플로팅 유지
      cardList.value = myCards;
      showCardSelectModal.value = true;
    }
    return;
  }

  // 내 비즈니스 목록 조회
  store.commit('business/initBusiness');
  await store.dispatch('business/getBusinessList', { userNo: user.userNo, isKakao: false, isOneKM: false });

  // 비즈니스 미등록
  if (isEmpty(myBusinessList.value) || myBusinessList.value.length === 0) {
    store.commit('layout/setIsFloating', false);
    showTextModal('비즈니스 등록 후 사용 가능합니다.', '확인', () => {
      router.push('/business/register/new');
    });
    return;
  }

  // 2. 비즈니스가 1개면 바로 실행, 여러 개면 선택 모달
  if (myBusinessList.value.length === 1) {
    executeSend(type, myBusinessList.value[0]);
  } else {
    // 여러 개면 선택 모달 — 플로팅 유지
    pendingSendType.value = type;
    showSelectModal.value = true;
  }
};

// 비즈니스 선택 시
const onSelectBusiness = (business) => {
  showSelectModal.value = false;
  executeSend(pendingSendType.value, business);
};

// 3. 실제 보내기 실행
const executeSend = async (type, business) => {
  // 상세 정보 조회
  await store.dispatch('business/getBusiness', business.businessNo);
  const detail = store.getters['business/getDetail'];

  if (type === 'catalog') {
    sendCatalog(detail);
  } else if (type === 'address') {
    sendAddress(detail);
  }
};

// 비즈니스 이미지 URL 가져오기
const getBusinessImageUrl = (business) => {
  // imageList에서 먼저 확인
  if (business?.imageList && business.imageList.length > 0) {
    return getImagePath(business.imageList[0].storedFileName);
  }
  // 로고 이미지 확인
  if (business?.logoStoredFileName) {
    return getImagePath(business.logoStoredFileName);
  }
  // preViewImage 확인
  if (business?.preViewImage?.storedFileName) {
    return getImagePath(business.preViewImage.storedFileName);
  }
  return `${CODE_ENV_LIST.FILE_SERVER_URL}/origin/AppIcon.png?v=2`;
};

// 공유 방식 선택 모달 표시
const showShareOptions = (title, description, imageUrl, shareUrl, buttonText) => {
  pendingShareData.value = { title, description, imageUrl, shareUrl, buttonText };
  showShareMethodModal.value = true;
};

// 공유 방식 실행
const executeShareMethod = (method) => {
  showShareMethodModal.value = false;
  store.commit('layout/setIsFloating', false);
  const data = pendingShareData.value;

  // 주소 공유는 별도 처리 (카카오 location 타입)
  if (data.type === 'address') {
    if (method === 'kakao') {
      sendAddressKakao(data.business);
    } else {
      sendAddressSms(data.business);
    }
    return;
  }

  const { title, description, imageUrl, shareUrl, buttonText } = data;
  if (method === 'kakao') {
    kakaoShare(title, description, imageUrl, shareUrl, buttonText);
  } else if (method === 'sms') {
    smsShare(title, description, shareUrl);
  }
};

// 카카오톡 공유
const kakaoShare = (title, description, imageUrl, shareUrl, buttonText) => {
  try {
    Kakao.Share.sendDefault({
      objectType: 'feed',
      content: {
        title: title,
        description: description,
        imageUrl: imageUrl,
        imageWidth: 800,
        imageHeight: 400,
        link: { webUrl: shareUrl, mobileWebUrl: shareUrl }
      },
      buttons: [{ title: buttonText, link: { webUrl: shareUrl, mobileWebUrl: shareUrl } }]
    });
  } catch (e) {
    const text = `${title}\n\n${description}\n\n${shareUrl}`;
    shareText(text, '내용이 복사되었습니다.');
  }
};

// 문자(SMS) 공유 — OG 엔드포인트 URL 사용 (미리보기 이미지 표시)
const smsShare = (title, description, shareUrl) => {
  // /card/share/123 → /api/og/card/123, /business/share/123 → /api/og/business/123
  const ogUrl = shareUrl.replace(/\/(card|business)\/share\//, '/api/og/$1/');
  const body = `${title}\n${description}\n${ogUrl}`;
  const smsUrl = `smsto:?body=${encodeURIComponent(body)}`;
  window.location.href = smsUrl;
};

// 카탈로그(기업소개) 보내기
const sendCatalog = (business) => {
  const shareUrl = `${CODE_ENV_LIST.DYNAMIC_DOMAIN}/business/share/${business.businessNo}`;
  const imageUrl = getBusinessImageUrl(business);

  showShareOptions(`비즈니스 소개장`, `비즈니스명 : ${business.name}`, imageUrl, shareUrl, '확인하기');
};

// 기업 주소 보내기 — 공유 방식 선택 후 실행
const sendAddress = (business) => {
  pendingShareData.value = { type: 'address', business };
  showShareMethodModal.value = true;
};

// 기업 주소 카카오 공유
const sendAddressKakao = (business) => {
  const imageUrl = getBusinessImageUrl(business);
  const address = business.roadAddress || business.address || '';
  const shareUrl = `${CODE_ENV_LIST.DYNAMIC_DOMAIN}/business/share/${business.businessNo}`;

  try {
    Kakao.Share.sendDefault({
      objectType: 'location',
      address: address,
      addressTitle: business.name,
      content: {
        title: `비즈니스 주소`,
        description: `${address} ${business.addressDetail ? '(' + business.addressDetail + ')' : ''}`.trim(),
        imageUrl: imageUrl,
        link: { webUrl: shareUrl, mobileWebUrl: shareUrl }
      },
      buttons: []
    });
  } catch (e) {
    let mapUrl;
    if (business.latitude && business.longitude) {
      mapUrl = `https://map.kakao.com/link/map/${encodeURIComponent(business.name)},${business.latitude},${business.longitude}`;
    } else {
      mapUrl = `https://map.kakao.com/link/search/${encodeURIComponent(address)}`;
    }
    shareText(`${business.name}\n\n${address}\n\n${mapUrl}`, '주소가 복사되었습니다.');
  }
};

// 기업 주소 문자 공유
const sendAddressSms = (business) => {
  const address = business.roadAddress || business.address || '';
  const ogUrl = `${CODE_ENV_LIST.DYNAMIC_DOMAIN}/api/og/business/${business.businessNo}`;
  const body = `비즈니스 주소\n${business.name}\n${address}\n${ogUrl}`;
  window.location.href = `smsto:?body=${encodeURIComponent(body)}`;
};

// 명함 선택 시
const onSelectCard = (card) => {
  showCardSelectModal.value = false;
  sendCard(card);
};

// 명함 보내기
const sendCard = async (card) => {
  const ogImageUrl = `${CODE_ENV_LIST.DYNAMIC_DOMAIN}/api/og/card/${card.cardNo}/image`;
  const shareUrl = `${CODE_ENV_LIST.DYNAMIC_DOMAIN}/card/share/${card.cardNo}`;

  showShareOptions(`[간판ON] ${card.name} 명함`, '명함을 확인해보세요', ogImageUrl, shareUrl, '명함 보기');
};

// 공유 유틸
const shareText = (text, copyMessage) => {
  if (isApp()) {
    nativeShareText(text);
  } else {
    navigator.clipboard.writeText(text).then(() => {
      if (!isIPhone() && !isIPad()) {
        showTextModal(copyMessage, '확인');
      }
    });
  }
};
</script>

<style scoped>
/* 공유 메뉴 — 가로 3원형 (Figma main_typeC 260612) */
.share-menu {
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-bottom: 9.4rem;
}
.share-menu__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 7rem;
  height: 7rem;
  padding-top: 1.4rem;
  border-radius: 50%;
  cursor: pointer;
}
.share-menu__item--intro {
  background: #ffc4d1;
  color: #5e0000;
}
.share-menu__item--address {
  background: #abd3ff;
  color: #03005e;
}
.share-menu__item--card {
  background: #dcb6ff;
  color: #2c0045;
}
.share-menu__ico {
  display: block;
  width: 2.4rem;
  height: 2.4rem;
  object-fit: contain;
}
.share-menu__label {
  margin-top: 0.4rem;
  font-size: 1.1rem;
  font-weight: 400;
  line-height: 130%;
  letter-spacing: -0.03em;
}

.business-select-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal-content {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  width: 80%;
  max-width: 360px;
  max-height: 60vh;
  overflow-y: auto;
}
.modal-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  text-align: center;
}
.business-select-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.business-select-item {
  padding: 14px 12px;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  margin-bottom: 8px;
  cursor: pointer;
}
.business-select-item:active {
  background: #f5f5f5;
}
.business-name {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 4px;
}
.business-address {
  font-size: 13px;
  color: #888;
}
</style>
